import { Auth, type User, getAuth } from "firebase/auth";

import React, { createContext, useContext, useEffect, useState } from "react";

import { useFirebase } from "../firebase/useFirebase";
import { onAuthChange } from "../monitoring";

type AuthState = {
  /**
   * Indicates if the auth state is still loading.
   *
   * Loading is set to false, when the auth state is known.
   *  - for a user that is logged in, the user is set
   *  - if a user wasn't logged in, user is set to null
   */
  loading: boolean;

  /**
   * If a user was logged in, this is the User, after loading this is the user.
   */
  user: User | null;

  auth: Auth;

  logout: () => Promise<void>;
};

const AuthStateCtx = createContext<AuthState>({} as AuthState);

type Props = {
  children: React.ReactNode;
  /**
   * Optional side-effect invoked whenever the Firebase auth state changes.
   * Apps can use this to dispatch Redux actions or set additional analytics
   * context.
   */
  onAuthChange?: (user: User | null) => void;
};

export const AuthStateProvider = ({
  children,
  onAuthChange: appOnAuthChange,
}: Props) => {
  // this is the user's loading state, not the auth instance itself
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const firebase = useFirebase();

  const auth = getAuth(firebase.firebaseApp);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      // analytics and tracking
      onAuthChange(user);
      // call back to update other parts of the app
      appOnAuthChange?.(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  return (
    <AuthStateCtx.Provider
      value={{
        loading,
        user,
        auth,
        logout: async () => {
          await auth.signOut();
        },
      }}
    >
      {children}
    </AuthStateCtx.Provider>
  );
};

export const useAuthState = () => useContext(AuthStateCtx);
